import { useEffect, useState } from "react"

import { minutesFromDayStart } from "./journal-time"

/**
 * Часы журнала.
 *
 * Линия «сейчас» обновляется не реже раза в минуту (ТЗ §2.2.1), а обратный отсчёт
 * удержания — раз в секунду (§2.4.1): администратор, разговаривая по телефону, решает
 * по этим цифрам, ждать ли освобождения времени. Поэтому таймеров два, а не один общий
 * на секунду: минутный таймер перерисовывает одну линию, и будить им всю сетку
 * шестьдесят раз в минуту незачем.
 */

/** Момент следующей круглой минуты. Шаг таймера считается от него, а не «через 60 с». */
function millisecondsToNextMinute(now: number): number {
  return 60_000 - (now % 60_000)
}

/**
 * Минуты от начала выбранного дня для «прямо сейчас» по зоне филиала.
 *
 * Возвращает значение всегда — решение, рисовать ли линию, принимает сетка: она одна
 * знает границы окна дня. Вчерашний и завтрашний день дадут число за пределами окна.
 */
export function useNowMinute(timeZone: string, date: string): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    // Таймер выравнивается по границе минуты: иначе линия «сейчас» переползает
    // на секунду позже с каждым тиком и к вечеру отстаёт на полминуты.
    const schedule = (): void => {
      timer = setTimeout(() => {
        setNow(Date.now())
        schedule()
      }, millisecondsToNextMinute(Date.now()))
    }
    schedule()
    return () => {
      clearTimeout(timer)
    }
  }, [])

  return minutesFromDayStart(new Date(now).toISOString(), timeZone, date)
}

/**
 * Секундный тик для обратного отсчёта удержаний.
 *
 * Включается только когда удержания есть: `enabled = false` не заводит таймер вовсе.
 * Удержание живёт пять минут, и держать секундный таймер весь день ради экрана,
 * на котором его ни разу не будет, — прямой расход батареи ноутбука за стойкой.
 */
export function useSecondsNow(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!enabled) return
    const timer = setInterval(() => {
      setNow(Date.now())
    }, 1000)
    return () => {
      clearInterval(timer)
    }
  }, [enabled])

  return now
}
